import type { DemandChangeResponse } from './api.js';

export interface DemandChangePollOptions {
  client: { pollDemandChanges(since?: number, signal?: AbortSignal): Promise<DemandChangeResponse> };
  /** Reconcile the list. `handshake` is true for the first response of a (re)connected loop. */
  refresh(handshake: boolean): Promise<void>;
  /**
   * Resume from a known cursor instead of handshaking — the shell bootstrap's cursor,
   * read before its embedded list (HS2-TMF3KZ). The first poll then replays any later
   * change; a reconnect after an error still handshakes.
   */
  initialCursor?: number;
  onError?(reason: unknown): void;
  /** Called after each successful response (and its reconcile), e.g. to end a fallback (HS2-HF0QT0). */
  onHealthy?(): void;
  retryMs?: number;
  maxRetryMs?: number;
  wait?: (milliseconds: number, signal: AbortSignal) => Promise<void>;
}

const abortableWait = (milliseconds: number, signal: AbortSignal): Promise<void> =>
  new Promise((resolve) => {
    const timer = setTimeout(resolve, milliseconds);
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        resolve();
      },
      { once: true },
    );
  });
const wasAborted = (signal: AbortSignal): boolean => signal.aborted;

/** Start one replay-safe demand-list long-poll loop; the returned function stops it. */
export function startDemandChangePoll(options: DemandChangePollOptions): () => void {
  const controller = new AbortController();
  const wait = options.wait ?? abortableWait;
  const retryMs = options.retryMs ?? 500;
  const maxRetryMs = options.maxRetryMs ?? 30_000;
  void (async () => {
    let cursor: number | undefined = options.initialCursor;
    let retryDelay = retryMs;
    for (;;) {
      try {
        const response: DemandChangeResponse = await options.client.pollDemandChanges(cursor, controller.signal);
        if (wasAborted(controller.signal)) return;
        const handshake = cursor === undefined;
        cursor = response.cursor;
        const shouldRefresh = handshake || response.overflow || response.events.length > 0;
        if (shouldRefresh) await options.refresh(handshake);
        retryDelay = retryMs;
        if (!wasAborted(controller.signal)) options.onHealthy?.();
      } catch (reason) {
        if (wasAborted(controller.signal)) return;
        options.onError?.(reason);
        cursor = undefined;
        await wait(retryDelay, controller.signal);
        retryDelay = Math.min(Math.max(retryDelay * 2, retryMs), maxRetryMs);
        if (wasAborted(controller.signal)) return;
      }
    }
  })();
  return () => {
    controller.abort();
  };
}
